// frontend/src/components/ORC/ORCPipeline.tsx

import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { orcService } from '../../services/orc.service';
import { tableService } from '../../services/table.service';
import './ORCPipeline.css';

type ORCRequest = {
  vendor: string;
  dataset: string;
  pipeline_version: number;
  previous_dataset_hash: string;
  query: string;
  target_name: string;
  user_story: string;
};

type ITableInfo = {
  table_name: string;
  columns: string[];
};

const AUTO_START_SECONDS = 5;

const ORCPipeline: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [userId, setUserId] = useState<number | null>(null);
  const [formData, setFormData] = useState<ORCRequest>({
    vendor: 'Postgres',
    dataset: '',
    pipeline_version: 0,
    previous_dataset_hash: '',
    query: '',
    target_name: '',
    user_story: '',
  });

  const [datasets, setDatasets] = useState<ITableInfo[]>([]);
  const [selectedDataset, setSelectedDataset] = useState<ITableInfo | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadingDatasets, setLoadingDatasets] = useState(true);
  const [error, setError] = useState('');

  // Auto-start
  const [countdown, setCountdown] = useState<number | null>(null);
  const [autoStartTriggered, setAutoStartTriggered] = useState(false);
  const countdownRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    const userStr = localStorage.getItem('user');
    if (!userStr) {
      setUserId(null);
      return;
    }
    try {
      const user = JSON.parse(userStr);
      setUserId(user.id ?? null);
    } catch {
      setUserId(null);
    }
  }, []);

  useEffect(() => {
    loadDatasets();
  }, []);

  const loadDatasets = async () => {
    try {
      setLoadingDatasets(true);
      const data = await tableService.getTablesAndColumns();
      const uniqueDatasets = data.filter(
        (item, index, self) =>
          index === self.findIndex(t => t.table_name === item.table_name)
      );
      setDatasets(uniqueDatasets);
    } catch (err: any) {
      setError(err.message || 'Erro ao carregar datasets');
    } finally {
      setLoadingDatasets(false);
    }
  };

  useEffect(() => {
    const state = location.state as any;
    const datasetName = state?.datasetName;
    if (!datasetName || datasets.length === 0) return;

    const match = datasets.find(d => d.table_name === datasetName);
    if (match) {
      setSelectedDataset(match);

      const targetName = state?.targetName ?? '';
      const userStory = state?.userStory ?? '';
      const autoStart = state?.autoStart === true;

      setFormData(prev => ({
        ...prev,
        dataset: match.table_name,
        target_name: targetName,
        user_story: userStory,
      }));

      navigate(location.pathname, { replace: true, state: {} });

      if (autoStart && targetName && userStory) {
        setCountdown(AUTO_START_SECONDS);
      }
    }
  }, [datasets, location.state, navigate]);

  useEffect(() => {
    if (countdown === null) return;
    if (countdown <= 0) {
      if (!autoStartTriggered) {
        setAutoStartTriggered(true);
        handleSubmitAuto();
      }
      return;
    }
    countdownRef.current = setInterval(() => {
      setCountdown(prev => (prev !== null ? prev - 1 : null));
    }, 1000);
    return () => {
      if (countdownRef.current) clearInterval(countdownRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [countdown]);

  const cancelAutoStart = () => {
    if (countdownRef.current) clearInterval(countdownRef.current);
    setCountdown(null);
    setAutoStartTriggered(false);
  };

  const handleSubmitAuto = async () => {
    if (!userId) {
      setError('Usuário não autenticado. Faça login primeiro.');
      return;
    }
    const payload = {
      userId,
      vendor: formData.vendor,
      dataset: formData.dataset,
      targetName: formData.target_name,
      userStory: formData.user_story,
      query: formData.query || '',
      pipeline_version: 0,
      previous_dataset_hash: '',
    };
    setLoading(true);
    setError('');
    try {
      const response = await orcService.process(payload);
      const traceId = response.trace_id || response.correlationId;
      if (!traceId) {
        setError('❌ Backend não retornou um trace_id.');
        setLoading(false);
        return;
      }
      navigate('/monitor', { state: { traceId, datasetName: formData.dataset } });
    } catch (err: any) {
      const errorMsg =
        err.response?.data?.error ||
        err.response?.data?.message ||
        err.message ||
        'Erro ao executar ORC';
      setError(`❌ ${errorMsg}`);
      setLoading(false);
    }
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === 'pipeline_version' ? parseInt(value) || 0 : value,
    }));
  };

  const handleDatasetSelect = async (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    if (!value) {
      setSelectedDataset(null);
      setFormData(prev => ({ ...prev, dataset: '', target_name: '' }));
      return;
    }
    try {
      const dataset = await tableService.getTableAndColumns(value);
      setSelectedDataset(dataset);
      setFormData(prev => ({ ...prev, dataset: value, target_name: '' }));
    } catch {
      setError('Erro ao carregar colunas do dataset');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (countdown !== null) cancelAutoStart();
    await handleSubmitAuto();
  };

  return (
    <div className="orc-pipeline-container">
      <h2>🔧 ORC Pipeline</h2>

      <div className="orc-content">
        {loading && (
          <div className="card-overlay">
            <div className="overlay-spinner" />
            <span className="overlay-text">🚀 Disparando pipeline...</span>
            <span className="overlay-subtext">Você será redirecionado para o acompanhamento</span>
          </div>
        )}

        {/* BANNER DE AUTO-START (OVERLAY) */}
        {countdown !== null && countdown > 0 && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.75)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
          }}>
            <div style={{
              background: 'linear-gradient(135deg, #1a1a2e, #16213e)',
              border: '2px solid #4fc3f7',
              borderRadius: '16px',
              padding: '40px',
              maxWidth: '500px',
              width: '90%',
              textAlign: 'center',
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.8)',
            }}>
              <div style={{ fontSize: '64px', marginBottom: '16px' }}>🚀</div>
              <h2 style={{ color: '#4fc3f7', margin: '0 0 12px 0', fontSize: '24px' }}>
                Processamento Automático
              </h2>
              <p style={{ color: '#e0e0e0', fontSize: '15px', lineHeight: 1.6, margin: '0 0 24px 0' }}>
                O dataset <strong>{formData.dataset}</strong> já está configurado.
                O processamento vai iniciar automaticamente em:
              </p>
              <div style={{ fontSize: '48px', fontWeight: 'bold', color: '#4fc3f7', margin: '0 0 24px 0' }}>
                {countdown}s
              </div>
              <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap' }}>
                <button
                  onClick={cancelAutoStart}
                  style={{
                    background: '#ef5350',
                    color: '#ffffff',
                    border: 'none',
                    borderRadius: '8px',
                    padding: '14px 28px',
                    fontWeight: 600,
                    cursor: 'pointer',
                    fontSize: '15px',
                  }}
                >
                  ✖️ Cancelar
                </button>
                <button
                  onClick={() => { cancelAutoStart(); handleSubmitAuto(); }}
                  style={{
                    background: 'transparent',
                    color: '#4fc3f7',
                    border: '1px solid #4fc3f7',
                    borderRadius: '8px',
                    padding: '14px 28px',
                    fontWeight: 600,
                    cursor: 'pointer',
                    fontSize: '15px',
                  }}
                >
                  ⚡ Iniciar Agora
                </button>
              </div>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="orc-form">
          <div className="form-group">
            <label>Vendor</label>
            <input type="text" value="Postgres" readOnly className="input-readonly" />
          </div>

          <div className="form-group">
            <label>Dataset *</label>
            <select name="dataset" value={formData.dataset} onChange={handleDatasetSelect} required>
              <option value="">Selecione um dataset...</option>
              {loadingDatasets ? (
                <option value="" disabled>Carregando...</option>
              ) : (
                datasets.map((dataset, index) => (
                  <option key={`${dataset.table_name}-${index}`} value={dataset.table_name}>
                    {dataset.table_name} ({dataset.columns.length} colunas)
                  </option>
                ))
              )}
            </select>
            {selectedDataset && (
              <div className="dataset-info">
                <span>Colunas disponíveis: </span>
                {selectedDataset.columns.join(', ')}
              </div>
            )}
          </div>

          <div className="form-group">
            <label>Query</label>
            <textarea name="query" value={formData.query} onChange={handleChange} placeholder="Ex: SELECT * FROM vendas_2024 WHERE valor > 1000" rows={4} />
            <small className="field-hint">Campo opcional</small>
          </div>

          <div className="form-group">
            <label>Target Name *</label>
            <select name="target_name" value={formData.target_name} onChange={handleChange} required disabled={!selectedDataset}>
              <option value="">Selecione um target...</option>
              {selectedDataset?.columns.map(col => (
                <option key={col} value={col}>{col}</option>
              ))}
            </select>
            {!selectedDataset && (
              <small className="field-hint">Selecione um dataset primeiro</small>
            )}
          </div>

          <div className="form-group">
            <label>User Story *</label>
            <textarea name="user_story" value={formData.user_story} onChange={handleChange} placeholder="Ex: Análise de vendas do mês de outubro" rows={6} required />
          </div>

          <button type="submit" disabled={loading} className="btn-primary">
            {loading ? '⏳ Disparando...' : '🚀 Executar ORC Pipeline'}
          </button>

          {error && <div className="error-message">❌ {error}</div>}
        </form>
      </div>
    </div>
  );
};

export default ORCPipeline;